import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  User,
  Settings as SettingsIcon,
  CreditCard,
  ShieldCheck,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/loved-one", label: "Loved One", icon: User },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function AppLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const showAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-72 shrink-0 border-r border-border/60 bg-white flex-col">
        <div className="px-6 py-6">
          <Logo size="md" />
        </div>
        <nav className="flex-1 px-4 py-3 flex flex-col gap-1">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {showAdmin && <NavItem to="/admin" label="Admin" icon={ShieldCheck} />}
        </nav>
        <div className="p-4 border-t border-border/60">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground grid place-items-center font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "K"}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <SettingsIcon className="h-4 w-4 mr-2" />Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing">
                <CreditCard className="h-4 w-4 mr-2" />Billing
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-destructive">
                <LogOut className="h-4 w-4 mr-2" />Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-border/60 flex items-center justify-between px-5 py-3">
        <Logo size="md" />
        <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 rounded-lg hover:bg-muted" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed top-14 inset-x-0 z-40 bg-white border-b border-border/60">
          <nav className="p-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} onClick={() => setMobileOpen(false)} />
            ))}
            {showAdmin && <NavItem to="/admin" label="Admin" icon={ShieldCheck} onClick={() => setMobileOpen(false)} />}
            <Button variant="ghost" onClick={doLogout} className="justify-start mt-2" data-testid="mobile-logout">
              <LogOut className="h-4 w-4 mr-2" />Log out
            </Button>
          </nav>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="lg:pt-0 pt-14">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 md:py-12">
            {(title || actions) && (
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 fade-in-up">
                <div>
                  {title && <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{title}</h1>}
                  {subtitle && <p className="text-muted-foreground mt-2 max-w-xl">{subtitle}</p>}
                </div>
                {actions && <div className="flex items-center gap-3">{actions}</div>}
              </div>
            )}
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}

function NavItem({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          isActive
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}
